import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Radio } from 'antd';
import { usePW910CF1Form } from './hooks/usePW910CF1Form';

const PW910CF1Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode, name, setName,
    idNumber, setIdNumber, birthDate, setBirthDate,
    maritalStatus, setMaritalStatus, gender, setGender,
    school, setSchool, department, setDepartment,
    gradYear, setGradYear, phone, setPhone,
    mobile, setMobile, address, setAddress,
    regAddress, setRegAddress, emergContact, setEmergContact,
    emergPhone, setEmergPhone, hometown, setHometown,
    applyDate, setApplyDate, applyDept, setApplyDept,
    applyTitle, setApplyTitle, startDate, setStartDate,
    height, setHeight, weight, setWeight,
    salary, setSalary, engName, setEngName,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = usePW910CF1Form();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除面試代號「${current.f90c_01}」(${current.f90c_11})？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) quickSearch(searchText.trim());
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          新進人員記錄表 (pw910cf1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入代號或姓名快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 300 }}
            maxLength={20}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields - Basic Info */}
        <div className="space-y-3 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                面試代號 (F90C_01) <span className="text-red-500">*</span>
              </label>
              <Input value={code} onChange={e => setCode(e.target.value)}
                maxLength={8} disabled={mode !== 'add'} style={{ width: 200 }}
                placeholder="最多8字元" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                姓名 (F90C_11) <span className="text-red-500">*</span>
              </label>
              <Input value={name} onChange={e => setName(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">身份證號 (F90C_12)</label>
              <Input value={idNumber} onChange={e => setIdNumber(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">英文名字 (F90C_55)</label>
              <Input value={engName} onChange={e => setEngName(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">出生日期 (F90C_13)</label>
              <Input value={birthDate} onChange={e => setBirthDate(e.target.value)}
                disabled={!isEditing} style={{ width: 160 }} placeholder="YYYY-MM-DD" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">性別 (F90C_15)</label>
              <Radio.Group value={gender} onChange={e => setGender(e.target.value)} disabled={!isEditing}>
                <Radio value={1}>男</Radio>
                <Radio value={2}>女</Radio>
              </Radio.Group>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">婚姻狀況 (F90C_14)</label>
              <Radio.Group value={maritalStatus} onChange={e => setMaritalStatus(e.target.value)} disabled={!isEditing}>
                <Radio value={1}>未婚</Radio>
                <Radio value={2}>已婚</Radio>
              </Radio.Group>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">最後就讀學校 (F90C_16)</label>
              <Input value={school} onChange={e => setSchool(e.target.value)}
                maxLength={40} disabled={!isEditing} style={{ width: '100%' }} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">科系 (F90C_17)</label>
                <Input value={department} onChange={e => setDepartment(e.target.value)}
                  maxLength={20} disabled={!isEditing} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">畢業年度 (F90C_18)</label>
                <Input value={gradYear} onChange={e => setGradYear(e.target.value)}
                  maxLength={10} disabled={!isEditing} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">聯絡電話 (F90C_19)</label>
              <Input value={phone} onChange={e => setPhone(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">行動電話 (F90C_20)</label>
              <Input value={mobile} onChange={e => setMobile(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">聯絡地址 (F90C_25)</label>
            <Input value={address} onChange={e => setAddress(e.target.value)}
              maxLength={60} disabled={!isEditing} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">戶籍地址 (F90C_26)</label>
            <Input value={regAddress} onChange={e => setRegAddress(e.target.value)}
              maxLength={60} disabled={!isEditing} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">緊急聯絡人 (F90C_27)</label>
              <Input value={emergContact} onChange={e => setEmergContact(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">緊急聯絡電話 (F90C_28)</label>
              <Input value={emergPhone} onChange={e => setEmergPhone(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 200 }} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">籍貫 (F90C_29)</label>
              <Input value={hometown} onChange={e => setHometown(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">身高(CM) (F90C_42)</label>
              <Input value={height} onChange={e => setHeight(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 100 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">體重(KG) (F90C_43)</label>
              <Input value={weight} onChange={e => setWeight(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 100 }} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">應聘部門 (F90C_32)</label>
              <Input value={applyDept} onChange={e => setApplyDept(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">應聘職稱 (F90C_33)</label>
              <Input value={applyTitle} onChange={e => setApplyTitle(e.target.value)}
                maxLength={10} disabled={!isEditing} style={{ width: 120 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">期望待遇(/月) (F90C_53)</label>
              <Input value={salary} onChange={e => setSalary(e.target.value)}
                maxLength={20} disabled={!isEditing} style={{ width: 160 }} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">應聘日期 (F90C_31)</label>
              <Input value={applyDate} onChange={e => setApplyDate(e.target.value)}
                disabled={!isEditing} style={{ width: 160 }} placeholder="YYYY-MM-DD" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">可開始上班日 (F90C_35)</label>
              <Input value={startDate} onChange={e => setStartDate(e.target.value)}
                disabled={!isEditing} style={{ width: 160 }} placeholder="YYYY-MM-DD" />
            </div>
          </div>
        </div>

        {/* Audit Fields (read-only) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.f90c_91} | 建立日期: {current.f90c_92 || '-'}</div>
            <div>修改人員: {current.f90c_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default PW910CF1Form;
